import { Grid } from "@mui/material";
import React, { useEffect } from "react";
import WSDataGrid, { IColumns } from "../../../Components/DataGrid/WSDataGrid";
import { useNavigate } from "react-router-dom";

import {
    callAPI,
    getLocalValue,
    LoacalVariables,
    Method
} from "../../../commonFunctions/CommonFunctions";
import { useSnackbar } from "../../../Components/Snackbar/SnackBarContext";
import { useDispatch } from "react-redux";
import { Helmet } from "react-helmet";
import { breadCrumbsStore } from "../../../Components/BreadCrumbs/BreadCrumbsListner";

const SanctionedLoanList: React.FC<any> = React.memo((props) => {
    const dispatch = useDispatch();
    const { showMessage } = useSnackbar();
    const navigate = useNavigate();
    const userType = getLocalValue(LoacalVariables.UserType);
    breadCrumbsStore.updateBreadCrumbs([{ "Santioned Loans": "/loan/requests/list" }]);

    const [loadingRegion, setLoadingRegion] = React.useState(false);
    const [districtOptions, setDistrictOptions] = React.useState<any[]>([]);
    const [blockOptions, setBlockOptions] = React.useState<any[]>([]);
    const [cdsOptions, setCdsOptions] = React.useState<any[]>([]);

    const source = {
        url: "loans/search",
        page: 1,
        pageSize: 10,
        filter: [
            { field: "status", operator: "eq", value: "sanctioned" }
        ],
        sort: []
    };

    useEffect(() => {
        if (userType === "admin") {
            fetchDistricts();
            fetchBlocks();
            fetchCDS();
            // fetchLoanTypes();
        } else if (userType === "district") {
            const districtId = getLocalValue(LoacalVariables.District);
            if (districtId) {
                fetchBlocks(districtId);
            }
        }
    }, []);

    const fetchDistricts = async () => {
        try {
            const res = await callAPI("districts/getAll", Method.GET);
            setDistrictOptions(
                res?.data?.map((d: any) => ({ label: d.name, value: d._id })) || []
            );
        } catch (error) {
            console.error("Failed to fetch districts:", error);
            showMessage("Failed to load districts", "error");
        }
    };

    const fetchBlocks = async (districtId?: string) => {
        try {
            setLoadingRegion(true);
            // const res = await callAPI(`blocks/byDistrict/${districtId}`, Method.GET);
            const res = await callAPI('blocks/getAll', Method.GET);

            setBlockOptions(
                res?.data?.map((b: any) => ({ label: b.name, value: b._id })) || []
            );
        } catch (error) {
            console.error("Failed to fetch blocks:", error);
            showMessage("Failed to load blocks", "error");
        } finally {
            setLoadingRegion(false);
        }
    };

    const fetchCDS = async (blockId?: string) => {
        try {
            // const res = await callAPI(`cds/byBlock/${blockId}`, Method.GET);
            const res = await callAPI('cds/getAll', Method.GET);

            setCdsOptions(
                res?.data.map((c: any) => ({ label: c.name, value: c._id })) || []
            );
        } catch (error) {
            console.error("Failed to fetch CDS:", error);
            showMessage("Failed to load CDS", "error");
        }
    };


    let limitedActions;

    switch (userType) {
        case 'admin':
            limitedActions = ["Disburse", "View", "Activity Logs"];
            break;
        case 'district':
            limitedActions = ["View", "Activity Logs"];
            break;
        case 'block':
            limitedActions = ["Disburse", "Edit", "View"];
            break;
        case 'cds':
            limitedActions = ["View"];
            break;
        default:
            limitedActions = ["View"];
    }

    //      const handleAction = async (action: string, row: any) => {
    //     if (action === "Edit") {
    //       setEdit(row);
    //       navigate(`/loan/request/${row?._id}`)
    //     } else if (action === "Delete") {
    //       setPopupOpen(true);
    //       setSelectedIdForDlt(row?._id);
    //     } else if (action === "Activity Logs") {
    //       navigate(`/loan/activityLogs/${row?._id}`)
    //     } else if (action === "View") {
    //       navigate(`/loan/detail/${row?._id}`)
    //     }
    //     else if (action === 'Sanction') {
    //       navigate(`/loan/request/sanction/${row?._id}`)
    //     }
    //   };

    //edit / delete or perform other actions.
    const handleAction = async (action: string, row: any) => {
        if (action === "Disburse") {
            navigate(`/loan/request/disburse/${row?._id}`)
        } else if (action === "Activity Logs") {
            navigate(`/loan/activityLogs/${row?._id}`)
        } else if (action === "View") {
            navigate(`/loan/detail/${row?._id}`)
        } else if (action === "Edit") {
            navigate(`/loan/request/${row?._id}`)
        }
    }

    const columns: IColumns[] = [
        {
            header: "Loan ID",
            fieldName: "loanId",
            tooltip: true,
            sortable: true
        },

        {
            header: "Enterprise",
            fieldName: "enterpriseName",
            tooltip: true,
            sortable: true
        },


        {
            header: "Sanctioned",
            fieldName: "sanctionedCefAmount",
            tooltip: true,
            sortable: true,
            type: "currency"
        },

        {
            header: userType === 'district' || userType === "admin" ? "Block" : "CDS",
            fieldName: userType === 'district' || userType === "admin" ? "block.name" : "cds.name",
            tooltip: true,
            type: "default"
        },

        {
            header: "Group Loan",
            fieldName: "isGroupLoan", // assuming the field is 'isGroupLoan'
            tooltip: true,
            sortable: true,
            type: 'default',
            render: (row: any) => row.isGroupLoan ? "Yes" : "No",  // converting true/false to Yes/No
        },

        {
            header: "Status",
            fieldName: "status",
            tooltip: true,
            type: "status"
        },

        {
            header: "Date",
            fieldName: "sanctionedDate",
            type: "date"
        },

        {
            header: "Actions",
            fieldName: "actions",
            type: "action",
            actions: limitedActions,
            actionCallback: (action, row) => handleAction(action, row)
        }
    ];

    const filterFields = [
        { type: "text", fieldName: "loanId", label: "Loan ID" },
        { type: "text", fieldName: "applicantName", label: "Applicant Name" },
        { type: "text", fieldName: "secondApplicantName", label: "Second Applicant Name" },
        { type: "text", fieldName: "mobile", label: "Mobile Number" },
        { type: "text", fieldName: "whatsappNumber", label: "WhatsApp Number" },
        { type: "text", fieldName: "enterpriseName", label: "Enterprise Name" },
        { type: "text", fieldName: "referenceNumber", label: "Reference Number" },

        // { type: "dropdown", fieldName: "loanType", label: "Loan Type", options: loanTypeOptions },
        // { type: "dropdown", fieldName: "district", label: "District", options: districtOptions },
        userType == 'district' && { type: "dropdown", fieldName: "block", label: "Block", options: blockOptions },
        { type: "dropdown", fieldName: "cds", label: "CDS", options: cdsOptions },


        { type: "date", fieldName: "createdAt", label: "Loan Created Date" },
        { type: "date", fieldName: "sanctionedDate", label: "Sanctioned Date" }
    ];

    return (
        <>
            <Helmet>
                <title>Loans | Willsoar Technologies</title>
                <meta
                    name="description"
                    content="Manage all loans in one place. View, add, or edit loan information easily."
                />
            </Helmet>
            <Grid p={2}>
                <Grid container spacing={2} mb={6}>
                    <Grid item xs={12}>
                        <WSDataGrid
                            id="loanGrid"
                            title="Sanctioned Loans"
                            columns={columns}
                            source={source}
                            filterFields={filterFields}
                            filters={[{ field: "status", operator: "eq", value: 'sanctioned' }]}
                            searchBox={{ fieldName: "mobile", type: "number" }}
                            placeholder={"Search by Mobile Number"}
                            gridHeaderSx={{
                                backgroundColor: "whitesmoke",
                                border: "1px solid whitesmoke"
                            }}
                            exportCsv
                            buttonStyle="Sigma"
                        />
                    </Grid>
                </Grid>
            </Grid>
        </>
    );
});


export default SanctionedLoanList;